/**
 * Mailchimp Account & Audience Discovery Module (Story 12.1, Task 2)
 *
 * Fetches Mailchimp account info and audience (list) data after successful OAuth.
 * Used for audience selection in per-client integration configuration (AC #3).
 *
 * All API calls use the data-center-specific base URL: https://{dc}.api.mailchimp.com/3.0/
 * Auth header: "Bearer {access_token}" (standard for API calls, unlike metadata which uses "OAuth")
 */

import type { AdapterErrorCode } from "../adapter";
import { safeFetch } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const DISCOVERY_TIMEOUT_MS = 10_000;

// ============================================================================
// Types
// ============================================================================

export interface MailchimpAccountInfo {
  accountId: string;
  accountName: string;
  email: string;
  dc: string;
}

export interface MailchimpAudience {
  id: string;
  name: string;
  memberCount: number;
  unsubscribeCount: number;
  dateCreated: string;
}

interface MailchimpRootResponse {
  account_id: string;
  account_name: string;
  email: string;
}

interface MailchimpListsResponse {
  lists: Array<{
    id: string;
    name: string;
    stats: {
      member_count: number;
      unsubscribe_count: number;
    };
    date_created: string;
  }>;
  total_items: number;
}

// ============================================================================
// Helper
// ============================================================================

/** Valid Mailchimp data center prefix pattern (e.g., "us19", "eu1") */
const DC_PATTERN = /^[a-z]{2}\d{1,3}$/;

/**
 * Builds the Mailchimp API base URL from the data center prefix.
 * @param dc - Data center prefix (e.g., "us19")
 * @throws if dc does not match expected format (SSRF protection)
 */
export function buildMailchimpApiBaseUrl(dc: string): string {
  if (!DC_PATTERN.test(dc)) {
    throw new Error(`Invalid Mailchimp data center prefix: "${dc}"`);
  }
  return `https://${dc}.api.mailchimp.com/3.0`;
}

// ============================================================================
// Account Info (Task 2.3)
// ============================================================================

/**
 * Fetches Mailchimp account info from the API root endpoint.
 *
 * @param accessToken - The OAuth access token
 * @param dc - Data center prefix (e.g., "us19")
 * @returns Account info or null on failure
 */
export async function fetchMailchimpAccountInfo(
  accessToken: string,
  dc: string
): Promise<MailchimpAccountInfo | null> {
  const baseUrl = buildMailchimpApiBaseUrl(dc);

  const result = await safeFetch(
    baseUrl,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) return null;

  const response = result.data;
  if (!response.ok) return null;

  try {
    const data = (await response.json()) as MailchimpRootResponse;
    if (!data.account_id) return null;

    return {
      accountId: String(data.account_id),
      accountName: data.account_name || "Mailchimp Account",
      email: data.email || "",
      dc,
    };
  } catch {
    return null;
  }
}

// ============================================================================
// Account Info with failure detail (Story 31.9)
// ============================================================================

/**
 * Discriminated result for {@link fetchMailchimpAccountInfoWithDetail}.
 *
 * Unlike the null-swallowing {@link fetchMailchimpAccountInfo} (kept as-is —
 * the OAuth callback depends on its signature), the `WithDetail` variant
 * carries the failure code + retryability so catalog discovery (Story 31.9 /
 * the 31-24 cron) can distinguish a revoked token from a transient 429/5xx.
 * Mirrors the Klaviyo `fetchKlaviyoAccountsWithDetail` pattern (Story 31.8).
 */
export type MailchimpAccountInfoDetailResult =
  | { ok: true; account: MailchimpAccountInfo }
  | {
      ok: false;
      code: AdapterErrorCode;
      message: string;
      retryable: boolean;
      /** Retry-After hint (ms) parsed by safeFetch on 429 — forwarded so the
       *  31-24 cron can honor provider backoff instead of blind-retrying. */
      retryAfterMs?: number;
    };

/**
 * Maps an HTTP status from the Mailchimp API root endpoint to an
 * AdapterErrorCode + retryability.
 *
 * Mirrors the `STATUS_CODE_MAP` semantics in ./errors.ts (AC #5):
 * - 401 → token_expired (NOT retryable — Mailchimp tokens are permanent,
 *   so a 401 means the token was REVOKED; a cron retry can never succeed)
 * - 403 → permission_denied (not retryable)
 * - 429 → rate_limited (retryable — concurrency-based limit, transient)
 * - 5xx → provider_unavailable (retryable)
 * - anything else → invalid_response (not retryable)
 */
function mapDiscoveryHttpStatus(
  status: number
): { code: AdapterErrorCode; retryable: boolean } {
  if (status === 401) return { code: "token_expired", retryable: false };
  if (status === 403) return { code: "permission_denied", retryable: false };
  if (status === 429) return { code: "rate_limited", retryable: true };
  if (status >= 500) return { code: "provider_unavailable", retryable: true };
  return { code: "invalid_response", retryable: false };
}

/**
 * Fetches Mailchimp account info from the API root endpoint
 * (`GET https://{dc}.api.mailchimp.com/3.0/`) with structured failure detail.
 *
 * Used by the adapter's catalog `discoverAccounts` (Story 31.9). Requests only
 * the fields the catalog needs (`?fields=account_id,account_name,email`) —
 * `total_subscribers` is deliberately excluded (volatile metric that would
 * churn the 31-24 cron's upsert diff on every run).
 *
 * Never throws on transport/HTTP/parse failures; only `buildMailchimpApiBaseUrl`
 * may throw on an invalid dc (callers validate dc first — SSRF guard).
 *
 * @param accessToken - The OAuth access token (Bearer auth — the root API uses
 *   `Bearer`; only login.mailchimp.com metadata uses the `OAuth` prefix)
 * @param dc - Data center prefix (e.g., "us19"), pre-validated by caller
 */
export async function fetchMailchimpAccountInfoWithDetail(
  accessToken: string,
  dc: string
): Promise<MailchimpAccountInfoDetailResult> {
  const baseUrl = buildMailchimpApiBaseUrl(dc);

  const result = await safeFetch(
    `${baseUrl}/?fields=account_id,account_name,email`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) {
    // safeFetch already coerces HTTP failures (401/403/429/5xx) and network
    // errors into AdapterErrorCode + retryable — pass through unchanged.
    return {
      ok: false,
      code: result.error.code,
      message: result.error.message,
      retryable: result.error.retryable,
      retryAfterMs: result.error.retryAfterMs,
    };
  }

  const response = result.data;
  if (!response.ok) {
    // Defensive: real safeFetch only returns success when response.ok, but
    // keep the STATUS_CODE_MAP-aligned mapping for mocked/alternate transports.
    const mapped = mapDiscoveryHttpStatus(response.status);
    return {
      ok: false,
      ...mapped,
      message: `Mailchimp API error: HTTP ${response.status}`,
    };
  }

  try {
    const data = (await response.json()) as MailchimpRootResponse;
    if (!data.account_id) {
      // Single-tenant platform: the tenant is always discoverable with a
      // healthy token — a 200 without account_id is a broken response.
      return {
        ok: false,
        code: "invalid_response",
        message: "Mailchimp API root response missing account_id",
        retryable: false,
      };
    }

    return {
      ok: true,
      account: {
        accountId: String(data.account_id),
        accountName: data.account_name || "Mailchimp Account",
        email: data.email || "",
        dc,
      },
    };
  } catch {
    return {
      ok: false,
      code: "invalid_response",
      message: "Failed to parse Mailchimp API root response",
      retryable: false,
    };
  }
}

// ============================================================================
// Audience/List Discovery (Task 2.3)
// ============================================================================

/**
 * Fetches all audiences (lists) for the connected Mailchimp account.
 *
 * Used in per-client integration configuration (AC #3) to let users
 * select which audience to pull reporting data from.
 *
 * @param accessToken - The OAuth access token
 * @param dc - Data center prefix (e.g., "us19")
 * @returns Array of audiences, or null on failure
 */
export async function fetchMailchimpAudiences(
  accessToken: string,
  dc: string
): Promise<MailchimpAudience[] | null> {
  const baseUrl = buildMailchimpApiBaseUrl(dc);

  const result = await safeFetch(
    `${baseUrl}/lists?count=100&fields=lists.id,lists.name,lists.stats,lists.date_created,total_items`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    DISCOVERY_TIMEOUT_MS
  );

  if (!result.success) return null;

  const response = result.data;
  if (!response.ok) return null;

  try {
    const data = (await response.json()) as MailchimpListsResponse;

    return (data.lists || []).map((list) => ({
      id: list.id,
      name: list.name,
      memberCount: list.stats?.member_count ?? 0,
      unsubscribeCount: list.stats?.unsubscribe_count ?? 0,
      dateCreated: list.date_created,
    }));
  } catch {
    return null;
  }
}
